import {
  BodyDefault,
  BodyStrong,
  HeadingItem,
  IconButton,
  MetaDefault,
  Tooltip,
} from '@terra/ui-design-system';
import { Box, HStack, VStack, token } from '@terra/ui-styled-system';
import { Cancel01Icon } from '@terra/ui-icons';
import { useTranslations } from 'next-intl';
import {
  formatTokenCount,
  type TokenUsageEstimate,
  type TokenUsageSegments,
} from './tokenUsageEstimate';
import type { CompactionActivity } from './compactionActivity';

type SegmentKey = keyof TokenUsageSegments;
type SegmentMeta = Record<
  SegmentKey,
  {
    label: string;
    color: string;
  }
>;

const SEGMENT_ORDER: SegmentKey[] = ['system', 'tools', 'conversation'];

function SegmentedBar({
  segments,
  effectiveWindowTokens,
  segmentMeta,
}: {
  segments: TokenUsageSegments;
  effectiveWindowTokens: number;
  segmentMeta: SegmentMeta;
}) {
  const widthOf = (value: number): string =>
    effectiveWindowTokens > 0
      ? `${Math.min((value / effectiveWindowTokens) * 100, 100)}%`
      : '0%';

  return (
    <Box h="2" w="full" bg="bg.muted" borderRadius="full" overflow="hidden">
      <HStack gap="2px" h="full" w="full" alignItems="stretch">
        {SEGMENT_ORDER.map((key) =>
          segments[key] > 0 ? (
            <Box
              key={key}
              h="full"
              borderRadius="full"
              flexShrink={0}
              style={{
                backgroundColor: segmentMeta[key].color,
                minWidth: '4px',
                width: widthOf(segments[key]),
              }}
            />
          ) : null,
        )}
      </HStack>
    </Box>
  );
}

function DistributionLegend({
  segments,
  segmentMeta,
}: {
  segments: TokenUsageSegments;
  segmentMeta: SegmentMeta;
}) {
  return (
    <VStack gap="2" alignItems="stretch">
      {SEGMENT_ORDER.map((key) => (
        <HStack key={key} justifyContent="space-between" alignItems="center">
          <HStack gap="2" alignItems="center">
            <Box
              w="2.5"
              h="2.5"
              borderRadius="xs"
              flexShrink={0}
              style={{ backgroundColor: segmentMeta[key].color }}
            />
            <BodyDefault color="fg.default">
              {segmentMeta[key].label}
            </BodyDefault>
          </HStack>
          <BodyDefault color="fg.muted">
            {formatTokenCount(segments[key])}
          </BodyDefault>
        </HStack>
      ))}
    </VStack>
  );
}

function GaugeBar({ estimate }: { estimate: TokenUsageEstimate }) {
  return (
    <Box
      position="relative"
      h="2"
      w="full"
      bg="bg.muted"
      borderRadius="full"
      overflow="hidden"
    >
      <Box
        position="absolute"
        left="0"
        top="0"
        h="full"
        borderRadius="full"
        bg={estimate.isOverTrigger ? 'fg.error' : 'colorPalette.default'}
        style={{ width: `${estimate.fillFraction * 100}%` }}
      />
      <Box
        position="absolute"
        top="0"
        h="full"
        w="2px"
        bg="fg.subtle"
        style={{ left: `${estimate.triggerMarkerFraction * 100}%` }}
      />
    </Box>
  );
}

export function TokenUsagePanel({
  estimate,
  compactionActivity,
  onClose,
}: {
  estimate: TokenUsageEstimate;
  compactionActivity?: CompactionActivity;
  onClose: () => void;
}) {
  const t = useTranslations('chat.copilot.tokenUsage');
  const percentOfWindow = Math.min(
    Math.round(estimate.usedFraction * 100),
    100,
  );
  const segmentMeta: SegmentMeta = {
    conversation: {
      color: token('colors.green.9'),
      label: t('conversation'),
    },
    system: { color: token('colors.sky.9'), label: t('systemPrompt') },
    tools: { color: token('colors.violet.9'), label: t('toolDefinitions') },
  };
  const compactionHint = compactionActivity?.isCompacting
    ? t('compactingNow')
    : estimate.tokensUntilCompaction === 0
      ? t('overCompactionThreshold', {
          percent: Math.round(estimate.triggerMarkerFraction * 100),
        })
      : t('untilAutoCompaction', {
          tokens: formatTokenCount(estimate.tokensUntilCompaction),
        });
  const compactionCount =
    compactionActivity?.compactionCount ?? estimate.compactionCount;
  const lastCompaction = compactionActivity?.lastCompaction;
  const lastCompactionHint =
    lastCompaction &&
    lastCompaction.tokensBefore !== null &&
    lastCompaction.tokensAfter !== null
      ? t('lastCompaction', {
          after: formatTokenCount(lastCompaction.tokensAfter),
          before: formatTokenCount(lastCompaction.tokensBefore),
        })
      : null;

  return (
    <Box
      bg="bg.canvas"
      borderWidth="1px"
      borderColor="border.subtle"
      borderRadius="xl"
      px="4"
      py="3.5"
    >
      <VStack gap="3" alignItems="stretch">
        <HStack justifyContent="space-between" alignItems="center">
          <HStack gap="1.5" alignItems="baseline">
            <HeadingItem color="fg.default">{t('context')}</HeadingItem>
            <MetaDefault color="fg.subtle">
              {estimate.orchestratorTokenSource === 'exact'
                ? t('devExact')
                : t('devEstimated')}
            </MetaDefault>
          </HStack>
          <Tooltip
            title={t('hideTokenUsage')}
            positioning={{ placement: 'top' }}
          >
            <IconButton
              type="button"
              variant="ghost"
              size="xs"
              aria-label={t('hideTokenUsage')}
              onClick={onClose}
              color="fg.muted"
              _hover={{ color: 'fg.default', bg: 'bg.subtle' }}
              borderRadius="full"
              minW="auto"
              h="auto"
              p="1"
            >
              <Cancel01Icon size={14} />
            </IconButton>
          </Tooltip>
        </HStack>

        <HStack justifyContent="space-between" alignItems="baseline">
          <BodyStrong
            color={estimate.isOverTrigger ? 'fg.error' : 'fg.default'}
          >
            {t('percentFull', { percent: percentOfWindow })}
          </BodyStrong>
          <BodyDefault color="fg.muted">
            {t('tokenCount', {
              used: formatTokenCount(estimate.orchestratorTokens),
              window: formatTokenCount(estimate.effectiveWindowTokens),
            })}
          </BodyDefault>
        </HStack>

        {estimate.segments ? (
          <SegmentedBar
            segments={estimate.segments}
            effectiveWindowTokens={estimate.effectiveWindowTokens}
            segmentMeta={segmentMeta}
          />
        ) : (
          <GaugeBar estimate={estimate} />
        )}

        {estimate.segments && (
          <DistributionLegend
            segments={estimate.segments}
            segmentMeta={segmentMeta}
          />
        )}

        <HStack justifyContent="space-between" alignItems="baseline">
          <MetaDefault color="fg.subtle">{compactionHint}</MetaDefault>
          {compactionCount > 0 && (
            <MetaDefault color="fg.subtle">
              {t('compactedCount', { count: compactionCount })}
            </MetaDefault>
          )}
        </HStack>
        {lastCompactionHint && (
          <MetaDefault color="fg.subtle">{lastCompactionHint}</MetaDefault>
        )}
      </VStack>
    </Box>
  );
}
